import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Check } from '@/components/Icons/Check';
import { Compose } from '@/components/Icons/Compose';
import { mailFolderIcons } from '@/pages/Mail/mailFolders';
import { ButtonVariant, MailFolderType } from '@/types/components';
import type { MailFolder } from '@/types/mail';
import { tv } from '@/utils/tv';

const tab = tv({
  base: 'flex h-[42px] cursor-pointer items-center gap-2 rounded-xl px-3 whitespace-nowrap transition-colors',
  variants: {
    isActive: {
      true: 'text-subhead-14 bg-info-surface text-primary',
      false:
        'text-body-14 text-muted-foreground hover:bg-info-surface hover:text-primary',
    },
  },
});

interface Props {
  folders: MailFolder[];
  folder: MailFolder;
  onChange: (folderType: MailFolderType) => void;
  onCompose: () => void;
}

export const MailFolderTabs: React.FC<Props> = ({
  folders,
  folder,
  onChange,
  onCompose,
}) => {
  const { t } = useTranslation();

  return (
    <div className="scrollbar-hidden flex items-center gap-4 overflow-x-auto rounded-[20px] bg-card p-2 shadow-card">
      <div className="flex w-fit shrink-0 rounded-[14px] border border-transparent bg-background contrast:border-border">
        {folders.map((item) => {
          const isActive = item.type === folder.type;

          return (
            <button
              key={item.type}
              type="button"
              onClick={() => onChange(item.type)}
              className={tab({ isActive })}
            >
              {mailFolderIcons[item.type]}
              <span className="grid">
                <span
                  aria-hidden
                  className="text-subhead-14 invisible col-start-1 row-start-1"
                >
                  {item.name}
                </span>
                <span className="col-start-1 row-start-1">{item.name}</span>
              </span>
              <span className="flex size-4 shrink-0 items-center justify-center">
                {isActive && (
                  <Check className="size-4 shrink-0" strokeWidth="1.8" />
                )}
              </span>
            </button>
          );
        })}
      </div>
      <Button
        variant={ButtonVariant.Primary}
        onClick={onCompose}
        className="ml-auto w-[145px] shrink-0 pl-3"
      >
        <Compose className="size-4.5" />
        {t('mail.compose')}
      </Button>
    </div>
  );
};
